import { utils, writeFileXLSX } from 'xlsx';
import { format } from 'date-fns';
import moment from 'moment-timezone';
import store from '@/core/common/services/vuex-store';
import { getRestriktionskonfiguration } from '@/core/restriktionen/services/restriktionskonfiguration.service.js';
import { germanCountryName } from '@/core/common/iso3166Countries.js';
import {
  RESTRIKTIONSTYP_OPTIONS,
  getRestriktionsgrundLabel,
  getWiederholungTab,
} from '@/components/restriktionen/restriktionen.constants.js';
import {
  WOCHENTAGE_OPTIONS,
  getRestriktionZeitraum,
} from '@/components/restriktionen/restriktionen-calendar.helper.js';

/**
 * @param {Object} config - Konfigurationsobjekt
 * @param {Array} config.headerRow - Header-Zeile für den Export
 * @param {Function} config.fetchData - Asynchrone Funktion zum Abrufen der Daten
 * @param {Function} config.createRowData - Funktion zum Formatieren einer Datenzeile
 * @param {string} config.sheetName - Name des Excel-Sheets
 * @param {string} config.fileNamePrefix - Prefix für den Dateinamen (z.B. 'flugplanung_export')
 * @returns {Promise<void>}
 */
export async function createExcelExport(config) {
  const { headerRow, fetchData, createRowData, sheetName, fileNamePrefix } = config;

  try {
    const exportData = [headerRow];
    const response = await fetchData();

    response.data.forEach(item => {
      const rowData = createRowData(item);
      exportData.push(...rowData);
    });

    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet(exportData);
    utils.book_append_sheet(wb, ws, sheetName);

    const fileName = `${fileNamePrefix}_${format(new Date(), 'dd-MM-yy_HH:mm')}.xlsx`;
    writeFileXLSX(wb, fileName);
  } catch (error) {
    console.error(`Export failed (${fileNamePrefix}):`, error);
    throw error;
  }
}

export const terminplanungHeaderRow = (language, type = 'standard') => {
  const headers = {
    standard: {
      de: [
        'Zusatztermin',
        'Reisekürzel',
        'Abreise',
        'VT Hin',
        'Status',
        'In Klärung',
        'In Blank angelegt',
        'Startdatum',
        'Hotelnächte',
        'Reisedauer',
        'TH Langstrecke',
        'TH Inlandsflug',
        'OB',
        'IB',
        'Flight Arrival',
        'Strecke',
        'Rückflugdatum',
        'VT Rück',
        'Ankunft DE',
        'FC Pax',
        'Erw. Termindurchführungsquote',
      ],
      en: [
        'Additional date',
        'Tour ID/Code',
        'Departure Germany',
        'Traffic day towards',
        'Status',
        'In Clarification',
        'Created in Blank',
        'Start Destination',
        'Hotel Nights',
        'Trip Duration',
        'TH Long-haul',
        'TH Domestic Flight',
        'OB',
        'IB',
        'Flight Arrival',
        'Route',
        'Return Flight Date',
        'Departure Destination',
        'Arrival Germany',
        'FC expected Pax',
        'Expected Schedule Completion Rate',
      ],
    },
    extern: {
      de: [
        'Reisekürzel',
        'Reiseterminkürzel',
        'Abreise',
        'OB',
        'Startdatum',
        'Hotelnächte',
        'Rückflugdatum',
        'IB',
        'Ankunft DE',
        'Status',
      ],
      en: [
        'Tour ID/Code',
        'Travel date abbreviation',
        'Departure Germany',
        'OB',
        'Start Destination',
        'Hotel Nights',
        'Departure Destination',
        'IB',
        'Arrival Germany',
        'Status',
      ],
    },
  };

  return headers[type]?.[language] || headers[type].en;
};

export const terminplanungSelectedReiseterminVariables = {
  standard: [
    'reiseterminkuerzel',
    'startdatum',
    'istZusatztermin',
    'garantiert',
    'abreisedatum',
    'hotelendUndRueckflugdatum',
    'state',
    'istInKlaerung',
    'verkehrstagHin',
    'verkehrstagRueck',
    'thLangstrecke',
    'thInlandsflug',
    'reiseterminSyncState',
    'reisedauer',
    'enddatum',
  ],
  extern: [
    'reiseterminkuerzel',
    'abreisedatum',
    'startdatum',
    'hotelendUndRueckflugdatum',
    'enddatum',
    'state',
  ],
};

export const terminplanungExpandConfig = {
  standard: {
    metadata: {
      expand: { ziellandAbflughaefen: {}, ziellandZielflughaefen: {} },
      select: [
        'overnightInbound',
        'overnightOutbound',
        'fruehesteAnkunft',
        'spaetesteAnkunft',
        'fruehesterAbflug',
        'spaetesterAbflug',
        'transferszenarioHin',
        'transferszenarioRueck',
        'hotelnaechte',
        'minPax',
        'maxPax',
        'forecastPax',
        'durchfuehrbareMinPax',
        'erwarteteTermindurchfuehrungsquote',
      ],
    },
    pax: { select: ['ist'] },
  },
  extern: {
    metadata: {
      select: ['overnightOutbound', 'hotelnaechte', 'overnightInbound'],
    },
  },
};

export const flugplanungHeaderRow = [
  'Zusatztermin',
  'Inlandsflug',
  'Reise',
  'Garantiert',
  'Vorz. garantiert',
  'Ist Pax',
  'FC Pax',
  'Abreise',
  'Rückflugdatum',
  'ZL Zielflughafen',
  'ZL Abflughafen',
  'Abflug INFL',
  'Strecke INFL',
  'Status',
  'Status Gruppe',
  'Einkaufsart',
  'Stop Sale',
  'Main/INFL',
  'OB',
  'IB',
  'Startdatum',
  'GRP FH',
  'GRP PNR',
  'Eingekaufte Pax',
  'Flugnr. GRP Hin',
  'Flugnr. GRP Rück',
  'INFL Flugnr.',
  'Hinflug VT',
  'Früh. Ankunft',
  'Spät Ankunft',
  'Rückflug VT',
  'Früh. Abflug',
  'Spät Abflug',
  'Ankunft DE',
  'TF Hin',
  'TF Rück',
  'Gesamtpreis GRP',
  'TH Langstrecke',
  'TH Inlandsflug',
  'TH Max Langstrecke',
  'TH Max Inlandsflug',
  'DZ p.P.',
  'Ticketdatum',
  'Optionsdatum',
  'TH Gesamt',
  'TH Max Gesamt',
  'Releasedatum',
  'Flugverfügbarkeit FRA',
];

export const flugplanungSelectedReiseterminVariables = [
  'reiseterminkuerzel',
  'startdatum',
  'optionsdatumFlugeinkauf',
  'istZusatztermin',
  'garantiert',
  'vorzeitigGarantiert',
  'abreisedatum',
  'hotelendUndRueckflugdatum',
  'state',
  'stopSale',
  'verkehrstagHin',
  'verkehrstagRueck',
  'enddatum',
  'thLangstrecke',
  'thInlandsflug',
  'ticketfristdatumFlugeinkauf',
  'ersterGruppenEinkaufPnr',
  'ersterGruppenEinkaufFlugnummerHin',
  'ersterGruppenEinkaufFlugnummerRueck',
  'basisPreis',
  'releasedatum',
];

export const flugplanungExpandConfig = {
  metadata: {
    expand: { inlandsfluege: {}, ziellandAbflughaefen: {}, ziellandZielflughaefen: {} },
    select: [
      'overnightInbound',
      'overnightOutbound',
      'fruehesteAnkunft',
      'spaetesteAnkunft',
      'fruehesterAbflug',
      'spaetesterAbflug',
      'transferszenarioHin',
      'transferszenarioRueck',
      'inlandsfluegeEinkauf',
      'restriktionenHinRollover',
      'restriktionenRueckRollover',
      'minPax',
      'maxPax',
      'kalkPax',
      'forecastPax',
      'durchfuehrbareMinPax',
    ],
  },
  pax: { select: ['ist', 'min', 'max', 'ist', 'opt', 'anf', 'war', 'vor'] },
  flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
  gueltigerSaisonpreis: {},
  flugverfuegbarkeiten: {
    orderby: 'abfragedatum desc',
    select: [
      'id',
      'aufpreis',
      'status',
      'abfragedatum',
      'deutscherAbflughafenIataCode',
      'abflughafenIataCode',
      'zielflughafenIataCode',
      'zimmertyp',
      'verpflegung',
      'isNeusteFraVerfuegbarkeit',
    ],
  },
};

export const flugstammdatenHeaderRow = [
  'Reise',
  'Abreise',
  'Rückflugdatum',
  'ZL Zielflughafen',
  'ZL Abflughafen',
  'Früh. Ankunft',
  'Spät. Ankunft',
  'Früh. Abflug',
  'Spät. Abflug',
  'TS Hin',
  'TS Rück',
  'VK kalk. Vornacht DZ p.P.',
  'TH Langstrecke',
  'Flinkok ZK',
  'Flinkok ZTV',
];

export const flugstammdatenSelectedReiseterminVariables = [
  'reiseterminkuerzel',
  'abreisedatum',
  'hotelendUndRueckflugdatum',
  'thLangstrecke',
  'flinkokZx',
  'flinkokZtv',
  'startdatum',
];

export const flugstammdatenExpandConfig = {
  metadata: {
    select: [
      'fruehesteAnkunft',
      'spaetesteAnkunft',
      'fruehesterAbflug',
      'spaetesterAbflug',
      'transferszenarioHin',
      'transferszenarioRueck',
      'vkVornachtDzProPerson',
      'restriktionenHinRollover',
      'restriktionenRueckRollover',
    ],
    expand: {
      ziellandAbflughaefen: {},
      ziellandZielflughaefen: {},
    },
  },
};

export const fvcHeaderRow = [
  'Reiseterminkürzel',
  'Verfügbarkeit',
  'Bearbeitung',
  'Zusatztermin',
  'Reisekürzel',
  'Abreisedatum',
  'Rückflugdatum',
  'ABFH',
  'Status',
  'Aufpreis',
  'Priorität',
  'Umsatzpotenzial',
  'TS Hin',
  'TS Rück',
  'Fehlermeldung',
  'Flugstamm Hin',
  'Hin-Nach',
  'Rück-Von',
  'PAX',
  'Gar.',
  'Einkaufsart',
  'Tage bis Release',
  'TH Langstrecke',
  'TH Max Langstrecke',
  'Zimmertyp',
  'RM',
  'Abfragedatum',
  'Zuletzt abgefragt',
  'Akzeptanzgrund',
  'Ursache',
];

export const fvcSelectedFlugverfügbarkeitVariables = [
  'bearbeitungStatus',
  'lastModifiedUserId',
  'deutscherAbflughafenIataCode',
  'status',
  'aufpreis',
  'flugstammHinflug',
  'zielflughafenIataCode',
  'abflughafenIataCode',
  'zimmertyp',
  'abfragedatum',
  'blankImportedDateUtc',
  'akzeptanzgrund',
  'ursache',
];

export const fvcExpandConfig = {
  reisetermin: {
    expand: {
      reise: { select: ['reisekuerzel', 'prioritaet', 'regionsmanager'] },
      flugeinkauf: { select: ['einkaufsart'] },
      metadata: { select: ['transferszenarioHin', 'transferszenarioRueck'] },
      gueltigerSaisonpreis: { select: ['thMaxLangstrecke'] },
      pax: { select: ['ist', 'min', 'max', 'ist', 'opt', 'anf', 'war', 'vor'] },
    },
    select: [
      'reiseterminkuerzel',
      'istZusatztermin',
      'abreisedatum',
      'hotelendUndRueckflugdatum',
      'umsatzpotenzial',
      'garantiert',
      'releasedatum',
      'thLangstrecke',
    ],
  },
  abfragefehler: {
    select: ['typ', 'text'],
  },
};

export const yieldControllingSelectedReiseterminVariables = [
  'reiseterminkuerzel',
  'startdatum',
  'optionsdatumFlugeinkauf',
  'istZusatztermin',
  'garantiert',
  'vorzeitigGarantiert',
  'abreisedatum',
  'hotelendUndRueckflugdatum',
  'state',
  'releasedatum',
  'basisPreis',
  'stopSale',
  'verkehrstagHin',
  'verkehrstagRueck',
  'thLangstrecke',
  'thInlandsflug',
  'durchfuehrungUnterMtzDurchSkr',
  'forecastPaxPotenzial',
];

export const yieldControllingExpandConfig = {
  metadata: {
    expand: {
      inlandsfluege: { select: ['einkauf', 'zielflughafenIataCode', 'abflughafenIataCode'] },
      ziellandAbflughaefen: {},
      ziellandZielflughaefen: {},
    },
    select: [
      'minPax',
      'maxPax',
      'kalkPax',
      'forecastPax',
      'durchfuehrbareMinPax',
      'planPax',
      'hotelnaechte',
      'transferszenarioHin',
      'transferszenarioRueck',
      'inlandsfluegeEinkauf',
      'fruehesteAnkunft',
      'spaetesteAnkunft',
      'fruehesterAbflug',
      'spaetesterAbflug',
      'restriktionenHinRollover',
      'restriktionenRueckRollover',
    ],
  },
  pax: { select: ['ist', 'min', 'max', 'ist', 'opt', 'anf', 'war', 'vor'] },
  countryManagers: { select: ['countryManagerKuerzel'] },
  reise: { select: ['reisekuerzel', 'Region'] },
  flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
  gueltigerSaisonpreis: { select: ['thMaxLangstrecke'] },
  zimmerkontingente: {
    filter: {
      isDeleted: false,
    },
    select: [
      'id',
      'zimmertyp',
      'isDeleted',
      'verfuegbarkeitsStatus',
      'einheitenFrei',
      'kontingentAktuell',
      'zuschlag',
      'kontingentAktuellGeteilt',
    ],
  },
  flugverfuegbarkeiten: {
    orderby: 'abfragedatum desc',
    select: [
      'id',
      'aufpreis',
      'status',
      'abfragedatum',
      'deutscherAbflughafenIataCode',
      'abflughafenIataCode',
      'zielflughafenIataCode',
      'zimmertyp',
      'verpflegung',
      'isNeusteFraVerfuegbarkeit',
    ],
    // filter: {
    //   abfragedatum: { ge: getDate14DaysAgo() },
    // },
  },
};

export const yieldControllingExportExpandConfig = {
  metadata: {
    expand: {
      inlandsfluege: { select: ['einkauf', 'zielflughafenIataCode', 'abflughafenIataCode', 'tag'] },
      ziellandAbflughaefen: {},
      ziellandZielflughaefen: {},
    },
    select: [
      'minPax',
      'maxPax',
      'kalkPax',
      'forecastPax',
      'durchfuehrbareMinPax',
      'planPax',
      'hotelnaechte',
      'transferszenarioHin',
      'transferszenarioRueck',
      'inlandsfluegeEinkauf',
      'fruehesteAnkunft',
      'spaetesteAnkunft',
      'fruehesterAbflug',
      'spaetesterAbflug',
      'restriktionenHinRollover',
      'restriktionenRueckRollover',
      'overnightOutbound',
    ],
  },
  pax: { select: ['ist', 'min', 'max', 'opt', 'anf', 'war', 'vor'] },
  countryManagers: { select: ['countryManagerKuerzel'] },
  reise: { select: ['reisekuerzel', 'region'] },
  flugeinkauf: { expand: { Gruppeneinkaeufe: {} } },
  gueltigerSaisonpreis: { select: ['thMaxLangstrecke', 'thMaxInlandsflug'] },
  flugverfuegbarkeiten: {
    filter: {
      isNeusteFraVerfuegbarkeit: true,
    },
    select: ['aufpreis', 'status', 'isNeusteFraVerfuegbarkeit'],
  },
};

export const yieldControllingHeaderRow = [
  'ZT',
  'Region',
  'Reisekürzel',
  'Reiseterminkürzel',
  'Status',
  'Abreise',
  'Rückflugdatum',
  'Min PAX',
  'DMin Pax',
  'Kalk Pax',
  'Erw Pax',
  'Ist Pax',
  'Opt Pax',
  'Anf Pax',
  'Max Pax',
  'Plan Pax',
  'Stop Sale',
  'Vorz. garantiert',
  'Garantiert',
  'Durchführung unter MTZ durch SKR',
  'Releasedatum',
  'Tage bis Release',
  'DZ Preis p.P',
  'Hotelnächte',
  'Transferszenario Hin',
  'Transferszenario Rück',
  'ZL Zielflughafen',
  'ZL Abflughafen',
  'INFL',
  'Abflug INFL',
  'Strecke INFL',
  'Hinflug VT',
  'ZL Zielflughäfen',
  'Früh. Ankunft',
  'Spät. Ankunft',
  'Rückflug VT',
  'ZL Abflughäfen',
  'Früh. Abflug',
  'Spät. Abflug',
  'TH Langstrecke',
  'TH Inlandsflug',
  'TH Max Langstrecke',
  'TH Max Inlandsflug',
  'Flugverfügbarkeit FRA',
  'Status Gruppe',
  'Einkaufsart',
  'GRP FH',
  'Gesamtpreis GRP',
  'FC Pax Potenzial',
  'CM',
];

export const sperrzeitenHeaderRow = [
  'Land',
  'Reisekürzel',
  'Typ',
  'Grund',
  'Wiederholung',
  'Beginn',
  'Ende',
  'Wochentage / Modus',
  'Angelegt von',
  'Angelegt am',
];

// Text der Wiederholung
function formatSperrzeitWiederholung(gruppe) {
  const tab = getWiederholungTab(gruppe);
  const turnus = gruppe.turnus || 1;
  switch (tab) {
    case 'woechentlich':
      return turnus > 1 ? `Alle ${turnus} Wochen` : 'Wöchentlich';
    case 'monatlich':
      return turnus > 1 ? `Alle ${turnus} Monate` : 'Monatlich';
    case 'jaehrlich':
      return 'Jährlich';
    default:
      return 'Einmalig';
  }
}

function formatSperrzeitWochentageModus(gruppe) {
  const tab = getWiederholungTab(gruppe);
  const wochentage = gruppe.wochentage || [];
  if (tab === 'woechentlich') {
    return WOCHENTAGE_OPTIONS.filter(option => wochentage.includes(option.value))
      .map(option => option.lang)
      .join(', ');
  }
  if (tab === 'monatlich') {
    if (wochentage.length) {
      const option = WOCHENTAGE_OPTIONS.find(eintrag => eintrag.value === wochentage[0]);
      return `${gruppe.ntesVorkommenImMonat || 1}. ${option ? option.lang : wochentage[0]}`;
    }
    return `Am ${moment(gruppe.startdatum).date()}. des Monats`;
  }
  return '';
}

function formatSperrzeitEnde(gruppe) {
  const { start, ende, istSingle } = getRestriktionZeitraum(gruppe);
  if (ende) {
    return ende.format('DD.MM.YYYY');
  }
  return istSingle ? start.format('DD.MM.YYYY') : 'Unbefristet';
}

// Eine Excel-Zeile pro Sperrzeit-Gruppe
function createSperrzeitenRowData(gruppe) {
  const typOption = RESTRIKTIONSTYP_OPTIONS.find(option => option.value === gruppe.restriktionstyp);
  return [
    (gruppe.laender || []).map(code => germanCountryName(code)).join(', '),
    (gruppe.reisekuerzel || []).join(', '),
    typOption ? typOption.text : gruppe.restriktionstyp,
    getRestriktionsgrundLabel(gruppe.restriktionsgrund),
    formatSperrzeitWiederholung(gruppe),
    moment(gruppe.startdatum).format('DD.MM.YYYY'),
    formatSperrzeitEnde(gruppe),
    formatSperrzeitWochentageModus(gruppe),
    store.getters.getUsernameById(gruppe.lastModifiedUserId) || '',
    gruppe.createdDateUtc ? moment(gruppe.createdDateUtc).format('DD.MM.YYYY') : '',
  ];
}

/**
 * @param {Object} config - Konfigurationsobjekt
 * @param {string} config.von - Beginn des Zeitraums (YYYY-MM-DD)
 * @param {string} config.bis - Ende des Zeitraums (YYYY-MM-DD)
 * @param {Array} config.laender - Länder-Filter (ISO-Alpha-2-Codes)
 * @param {Array} config.reisen - Reise-Filter (Reisekürzel)
 * @param {string} config.fileNamePrefix - Prefix für den Dateinamen
 * @returns {Promise<void>}
 */
export async function createSperrzeitenExcelExport({ von, bis, laender, reisen, fileNamePrefix }) {
  return createExcelExport({
    headerRow: sperrzeitenHeaderRow,
    fetchData: async () => {
      const result = await getRestriktionskonfiguration({ von, bis, laender, reisen });
      const gruppen = [...((result && result.laender) || []), ...((result && result.reisen) || [])];
      // Gruppen können in Länder- und Reise-Zuordnung doppelt auftauchen,
      // exportiert werden nur Sperrzeiten mit geblockten Tagen im Zeitraum
      const bekannt = new Set();
      const data = gruppen.filter(gruppe => {
        if (bekannt.has(gruppe.id) || !(gruppe.restriktionen || []).length) {
          return false;
        }
        bekannt.add(gruppe.id);
        return true;
      });
      // Zeilen chronologisch nach Beginn sortiert, ISO-Strings sortieren lexikografisch korrekt
      data.sort((a, b) => (a.startdatum || '').localeCompare(b.startdatum || ''));
      return { data };
    },
    createRowData: gruppe => [createSperrzeitenRowData(gruppe)],
    sheetName: 'Sperrzeiten',
    fileNamePrefix,
  });
}
